import _ from 'lodash';
import { createSelector, lruMemoize } from 'reselect';
import { CATEGORY_TYPES } from '../../constants';
import { getParentIdByPath } from '../../utils';
import { isExternalSyncReadOnly, isPolicyTechSynced } from '../../utils/knowledge.utils';
import workspaceService from '../../services/workspace.service';
import { getUser } from './user.selector';
import { hasPermissionsToEditItem } from './iam.selector';
import { findContactInSlices } from './contact.selector';

export const getByIdCategory = (category, idToSearch) => {
  if (category.children && category.children.length !== 0) {
    let result;
    category.children.forEach(item => {
      if (result) return false;
      let res = getByIdCategory(item, idToSearch);

      if (res) result = res;
    });

    return result;
  }

  if (category.knowledge_item_medias && category.knowledge_item_medias !== 0) {
    return category.knowledge_item_medias.find(({ id }) => idToSearch === id);
  }
};

export const getKnowledgeItemById = ({ knowledge }, id) => {
  return getItemById(knowledge, { id });
};

// Memoized: the contact lookup builds a fresh { ...user } for user-editors, which
// tripped react-redux's stabilityCheck. Keyed on the state slices + resolved id so
// a repeat call with unchanged state returns the same reference.
export const getLastEditedBy = createSelector(
  [
    ({ contacts }) => contacts?.contactsById,
    ({ usersManagement }) => usersManagement?.list,
    (_state, knowledge) =>
      knowledge?.revisionLog?.length ? knowledge.revisionLog[0].userId : knowledge?.updatedBy,
  ],
  findContactInSlices,
);

export const getRoot = ({ categories }, initialWorkspaceId = null) => {
  //support orgs with and without workspaces
  if (!workspaceService.hasWorkspaces()) {
    if (!categories) return {};
    return categories.find(category => category.path?.toLowerCase() === 'root');
  }
  let workspaceId = initialWorkspaceId;

  if (!workspaceId) {
    workspaceId = workspaceService.getSelectedWorkspaceId();
  }

  if (!categories || !workspaceId) return {};
  return (
    categories.find(
      category => category.path?.toLowerCase() === 'root' && category.workspaceId === workspaceId,
    ) ?? {}
  );
};

export const getCategoriesWithLinkedItem = ({ itemsById }, id) => {
  return itemsById[id]?.categoriesInfo?.map(({ categoryId }) => categoryId) || [];
};

export const getCategoriesByPath = ({ categories, categoriesById }, path) => {
  if (_.isNull(categories)) return null;

  let parentId = getParentIdByPath(path);

  return getCategoriesById({ categoriesById }, parentId);
};

export const getCategoriesById = ({ categoriesById }, id) => {
  if (_.isNull(categoriesById)) return null;

  if (!categoriesById[id]) return null;

  return categoriesById[id]?.subcategories.map(i => getCategoryById({ categoriesById }, { id: i }));
};

export const getCategoryById = ({ categoriesById }, { id }) => {
  return !_.isNull(categoriesById) ? (categoriesById[id] ?? {}) : {};
};

// Call sites build `ids` inline (`[id]`, `sections.map(...)`), so a fresh array every
// render would defeat identity-based memoization — compare arrays by content instead.
const shallowArrayEqual = (a, b) =>
  a === b ||
  (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]));

const externalSyncDescendantMemoOptions = {
  equalityCheck: shallowArrayEqual,
  // One entry per mounted section (each passes its own ids/predicate).
  maxSize: 50,
};

/**
 * True when any category in the subtrees rooted at `ids` matches `predicate`.
 * Store nodes hold child ids in `subcategories` (not nested objects), so the walk
 * resolves them through `categoriesById`. Mirrors are skipped: a mirror carries its
 * source's options, so a mirror of a synced binder would otherwise lock the normal
 * workspace hosting it. Memoized on (categoriesById, ids, predicate) so an unrelated
 * store change doesn't re-walk the tree.
 */
const hasExternalSyncDescendant = (categoriesById, ids, predicate) => {
  // Mirrored subtrees make revisits real, and a cycle must not blow the stack.
  const visited = new Set();
  const matches = id => {
    if (visited.has(id)) return false;
    visited.add(id);
    const node = categoriesById?.[id];
    if (!node || node.mirroredFrom) return false;
    return predicate(node.options) || (node.subcategories ?? []).some(matches);
  };
  return (ids ?? []).some(matches);
};

const selectHasExternalSyncDescendant = createSelector(
  [
    ({ knowledge }) => knowledge?.categoriesById,
    (_state, ids) => ids,
    (_state, _ids, predicate) => predicate,
  ],
  hasExternalSyncDescendant,
  {
    memoize: lruMemoize,
    memoizeOptions: externalSyncDescendantMemoOptions,
    argsMemoize: lruMemoize,
    argsMemoizeOptions: externalSyncDescendantMemoOptions,
  },
);

export const hasExternalSyncDescendantById =
  (ids, predicate = isExternalSyncReadOnly) =>
  state =>
    selectHasExternalSyncDescendant(state, ids, predicate);

/**
 * Workspaces whose knowledge tree holds PolicyTech categories. The provider mirrors the
 * whole workspace, so its root is as read-only as a synced binder — but the root is not a
 * binder, so the per-binder guards never see it. Destination pickers (mirror, move, hub
 * publish) drop these workspaces from their options.
 */
export const getPolicyTechWorkspaceIds = createSelector(
  [({ knowledge }) => knowledge?.categories, ({ knowledge }) => knowledge?.categoriesById],
  (categories, categoriesById) =>
    (categories ?? [])
      .filter(
        category =>
          category.path?.toLowerCase() === 'root' &&
          category.workspaceId &&
          hasExternalSyncDescendant(categoriesById, [category.id], isPolicyTechSynced),
      )
      .map(category => category.workspaceId),
);

export const getItemById = ({ itemsById }, { id }) => {
  return itemsById?.[id] || null;
};

export const getItemsByIds = ({ itemsById }, ids) => {
  return ids?.map(id => itemsById?.[id] || {}).filter(i => i?.id) || [];
};

export const generateBreadcrumbs = (
  { categoriesById, categories },
  { pathParts = [], initialWorkspaceId = null },
) => {
  const rootId = getRoot({ categories }, initialWorkspaceId)?.id;

  return pathParts
    ? pathParts
        .filter(part => part.toLowerCase() !== 'root' && part !== rootId)
        .map(id => categoriesById[id])
    : [];
};

/**
 * Check if the category is a section
 * Re-exported from category.utils.js for backward compatibility
 * if path contains root, sections are items that have an odd number of path parts
 * if path does not contain root, sections are items that have an even number of path partss
 * @param {*} pathParts
 * @returns boolean
 */
export { isSection } from '../../utils/category.utils';

export const getKnowledgeParentsByItemId = ({ categories }, { id }) => {
  return categories.filter(
    cat => cat.type === 'knowledge' && !!cat.knowledgeItems.find(item => item.id === id),
  );
};

/**
 * Binder links of each knowledge item, from the loaded categories.
 *
 * @param {object} state - Redux state.
 * @returns {Map<string, Array<{ categoryId: string, workspaceId: string, linked?: boolean, ownerIds?: string[] }>>}
 *   Links by item id.
 * @example
 * const links = getKnowledgeLinksByItemId(state).get(item.id) ?? [];
 */
export const getKnowledgeLinksByItemId = createSelector(
  state => state.knowledge?.categories,
  categories => {
    const linksByItemId = new Map();
    (categories ?? [])
      .filter(category => category.type === CATEGORY_TYPES.KNOWLEDGE)
      .forEach(category =>
        (category.knowledgeItems ?? []).forEach(({ id, linked, ownerIds }) => {
          if (!linksByItemId.has(id)) linksByItemId.set(id, []);
          linksByItemId.get(id).push({
            categoryId: category.id,
            workspaceId: category.workspaceId,
            linked,
            ownerIds,
          });
        }),
      );
    return linksByItemId;
  },
);

export const getKnowledgeParentsByItemIdWithWorkspace = ({ categories }, workspace, { id }) => {
  if (!categories || !workspace?.list?.length) return { paths: [], existsInOnlyOneWorkspace: true };

  const parents = getKnowledgeParentsByItemId({ categories }, { id });

  const workspaceIds = new Set(parents.map(p => p.workspaceId));
  const pathWorkspaces = workspace.list.filter(ws => workspaceIds.has(ws.id));
  const workspaceById = new Map(pathWorkspaces.map(ws => [ws.id, ws]));

  const paths = parents
    .map(cat => ({
      ...cat,
      workspaceName: workspaceById.get(cat.workspaceId).name,
      linked: !!cat.knowledgeItems.find(item => item.id === id)?.linked,
    }))
    .sort((a, b) => a.workspaceName.localeCompare(b.workspaceName));

  const existsInOnlyOneWorkspace = workspaceIds.size === 1;

  return { paths, existsInOnlyOneWorkspace };
};

export const isKnowledgeReady = ({ knowledge }) => knowledge.isReady;

export const getItemCategories = ({ knowledge }, { id }) => {
  // categories is null until getKnowledgeContent() loads; return [] so callers
  // that .map() the result don't crash when the move dialog opens mid-load.
  return (
    knowledge.categories?.filter(cat => cat?.knowledgeItems?.find(item => item.id === id)) ?? []
  );
};

export const getItemCategory = createSelector(
  [({ knowledge }) => knowledge.categories, (_state, { id }) => id],
  (categories, id) =>
    categories?.find(cat => cat?.knowledgeItems?.find(item => item.id === id && !item.linked)),
);

export const getKeywordsByItemId = ({ itemsById }, { id }) => {
  return itemsById[id]?.keywords || [];
};

export const isParentSharedToHub =
  path =>
  ({ knowledge }) => {
    const { categoriesById } = knowledge;
    const id = getParentIdByPath(path);
    if (!id) return false;

    const parent = categoriesById[id];
    if (!parent) return false;

    return parent.shared?.isShared ?? false;
  };

/**
 * The items of a binder joined from the catalog in the store, in binder order, with the
 * link flags. The web and mobile binder pages fetch rows instead; widget-app and the
 * offline fallback still read the catalog.
 *
 * @param {{ itemsById: object, categoriesById: object }} knowledge - The knowledge slice.
 * @param {{ id: string }} params - The binder id.
 * @returns {object[]} Items with `linked`, `linkOwnerIds` and `isSourceDerived`.
 * @example
 * const items = getItemsByCategoryId(state.knowledge, { id });
 */
export const getItemsByCategoryId = ({ itemsById, categoriesById }, { id }) => {
  const { knowledgeItems = [] } = !_.isNull(categoriesById) ? (categoriesById[id] ?? {}) : {};

  return knowledgeItems
    .map(({ id, linked, ownerIds = null, isSourceDerived, ...itemData }) => {
      const item = itemsById?.[id] || null;

      if (!!item && linked) {
        return {
          ...item,
          linked,
          linkOwnerIds: itemData.linkOwnerIds ?? ownerIds,
          isSourceDerived,
        };
      }
      if (!item) return null;
      return { ...item, isSourceDerived };
    })
    .filter(i => !!i);
};

/**
 * `getItemsByCategoryId` memoized for `useSelector`: the join builds new rows on every
 * call, which re-rendered on every store update and tripped react-redux's
 * stabilityCheck. Returns the same array while the binder's tree and rows are unchanged.
 *
 * @param {{ itemsById: object, categoriesById: object }} knowledge - The knowledge slice.
 * @param {string} id - The binder id.
 * @returns {object[]} Items with `linked`, `linkOwnerIds` and `isSourceDerived`.
 * @example
 * const items = useSelector(state => selectItemsByCategoryId(state.knowledge, id));
 */
export const selectItemsByCategoryId = createSelector(
  [knowledge => knowledge.itemsById, knowledge => knowledge.categoriesById, (_knowledge, id) => id],
  (itemsById, categoriesById, id) => getItemsByCategoryId({ itemsById, categoriesById }, { id }),
);

/**
 * Whether the user may delete a category: an editor permission, or owner of the category,
 * every category below it, every link in them and every item in them.
 *
 * @param {ReduxState} state
 * @param {{ id: string }} params - The category id.
 * @param {Record<string, { ownerIds?: string[] }>} itemsById - Rows of the items below the
 *   category (`useKnowledgeItemsById`); an item without a row counts as not owned.
 * @return {boolean}
 * @example
 * const canDelete = isKnowledgeOwner(state, { id: category.id }, itemsById);
 */
export function isKnowledgeOwner(state, { id }, itemsById = {}) {
  if (!id) return false;

  if (hasPermissionsToEditItem(state)) return true;

  const { id: userId } = getUser(state);

  return checkChildrenOwnership(state, id, userId, itemsById);
}

function checkChildrenOwnership(state, id, userId, itemsById) {
  const item = getCategoryById(state.knowledge, { id });

  if (item.ownerIds && !item.ownerIds.includes(userId)) {
    return false;
  }

  if (item.type === CATEGORY_TYPES.CATEGORY) {
    return item.subcategories.every(subcategoryId =>
      checkChildrenOwnership(state, subcategoryId, userId, itemsById),
    );
  }

  return item.knowledgeItems.every(({ id, linked, ownerIds: linkOwnerIds }) => {
    if (linked) {
      return linkOwnerIds.includes(userId);
    }

    return itemsById[id]?.ownerIds?.includes(userId) ?? false;
  });
}

/**
 * Check if the user have permissions to edit the item
 *
 * @param {ReduxState} state
 * @param {{ ownerIds: string[], linkOwnerIds: string[], linked: boolean, ownerIds: string[] }}
 * @return {boolean} if the user have permissions to edit the item
 */
export function haveEditPermissions(state, { linkOwnerIds, linked, ownerIds }) {
  const hasPermissions = hasPermissionsToEditItem(state);

  if (hasPermissions) return true;

  const { id: userId } = getUser(state);

  if (linked) {
    return linkOwnerIds.includes(userId);
  }

  if (ownerIds && ownerIds.length > 0) return ownerIds.includes(userId);

  return false;
}

/**
 * Get all binders (knowledge type categories) that have at least one item
 * Used for filter dropdowns to show only relevant categories
 *
 * @param {{ knowledge: { categories: Array } }} state
 * @returns {Array<{ id: string, title: string, path: string }>} Array of categories with items
 */
export const getCategoriesWithItems = ({ knowledge }) => {
  const { categories } = knowledge;

  if (!categories) return [];

  return categories
    .filter(
      category =>
        category.type === CATEGORY_TYPES.KNOWLEDGE &&
        category.knowledgeItems &&
        category.knowledgeItems.length > 0,
    )
    .map(({ id, title, path }) => ({ id, title, path }));
};

/**
 * Get all knowledge item IDs under a category recursively
 * This includes items from all subcategories
 *
 * @param {{ categoriesById: Object }} knowledge state
 * @param {Object} categoriesById - The category mapping by ID
 * @param {string} categoryId - The category ID to start from
 * @returns {string[]} Array of knowledge item IDs
 */
export const getAllKnowledgeItemIdsUnderCategory = ({ categoriesById, categoryId }) => {
  const itemIds = new Set();

  const collectItems = catId => {
    const category = categoriesById?.[catId];
    if (!category) return;

    category.knowledgeItems?.forEach(({ id }) => itemIds.add(id));
    category.subcategories?.forEach(subId => collectItems(subId));
  };

  collectItems(categoryId);
  return Array.from(itemIds);
};
